import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzPaginationComponent } from 'ng-zorro-antd/pagination';

import { TranslocoDirective } from '@jsverse/transloco';

import {
  MbSearchComponent,
  MbSkeletonComponent,
  ToastService,
  MbModalComponent,
} from '@components/common';

import { UmuturageData, UmuturageService } from '@service/umuturage.services';

@Component({
  selector: 'app-umuturage',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzMenuModule,
    NzDropDownModule,
    NzModalModule,
    NzTableModule,
    NzAvatarModule,
    NzPaginationComponent,
    MbSearchComponent,
    MbSkeletonComponent,
    MbModalComponent,
    TranslocoDirective,
  ],
  templateUrl: './umuturage.component.html',
})
export class UmuturageComponent implements OnInit {
  private readonly umuturageService = inject(UmuturageService);
  private readonly toastService = inject(ToastService);

  allData: UmuturageData[] = [];
  filteredData: UmuturageData[] = [];
  paginatedData: UmuturageData[] = [];

  selectedUmuturage: UmuturageData | null = null;

  isDetailsModalVisible = false;
  isLoading = false;

  searchValue = '';

  pageIndex = 1;
  pageSize = 50;
  totalItems = 0;

  ngOnInit(): void {
    this.loadUmuturageData();
  }

  loadUmuturageData(): void {
    this.isLoading = true;

    this.umuturageService.getAllUmuturageData().subscribe({
      next: (data: UmuturageData[]) => {
        this.allData = data;
        this.filteredData = [...data];

        this.totalItems = data.length;

        this.pageIndex = 1;

        this.updatePage();

        this.isLoading = false;
      },

      error: (error) => {
        this.isLoading = false;

        this.toastService.show(
          error?.message ?? 'Failed to load Umuturage data',
          'error',
        );
      },
    });
  }

  onSearch(value: string): void {
    this.searchValue = value ?? '';

    const term = this.searchValue.trim().toLowerCase();

    if (!term) {
      this.filteredData = [...this.allData];
    } else {
      this.filteredData = this.allData.filter(
        (item) =>
          this.contains(item.amazina, term) ||
          this.contains(item.indangamuntu, term) ||
          this.contains(item.telephone, term) ||
          this.contains(item.districtName, term) ||
          this.contains(item.categoryName, term) ||
          this.contains(item.groupName, term) ||
          this.contains(item.provinceName, term) ||
          this.contains(item.imiterere, term) ||
          this.contains(item.icyakorwa, term) ||
          this.contains(item.icyakozwe, term) ||
          this.contains(item.umwanzuro, term) ||
          this.contains(item.statusName, term),
      );
    }

    this.totalItems = this.filteredData.length;

    // Always return to first page after searching
    this.pageIndex = 1;

    this.updatePage();
  }

  onClearSearch(): void {
    this.searchValue = '';

    this.filteredData = [...this.allData];

    this.totalItems = this.filteredData.length;

    this.pageIndex = 1;

    this.updatePage();
  }

  private contains(value: unknown, term: string): boolean {
    return String(value ?? '')
      .toLowerCase()
      .includes(term);
  }

  updatePage(): void {
    const startIndex = (this.pageIndex - 1) * this.pageSize;

    const endIndex = startIndex + this.pageSize;

    this.paginatedData = this.filteredData.slice(startIndex, endIndex);
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;

    this.updatePage();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;

    this.pageIndex = 1;

    this.updatePage();
  }

  showDetailsModal(value: UmuturageData | null): void {
    this.selectedUmuturage = value;

    this.isDetailsModalVisible = !!value;
  }
}
